<template>
  <div class="revision-selector flex items-center" data-test="revision-selector-container">
    <button
      v-if="enableHistoricalRevisions"
      class="pathLink color-theme-light hover:color-theme-base hover:underline transition-all duration-300 ease-in-out"
      aria-label="Select historical revision"
      :aria-expanded="isOpen.toString()"
      aria-haspopup="dialog"
      :disabled="isLoading"
      data-test="revision-selector-button"
      @click="openModal"
    >
      <BaseIcon
        :icon="isLoading ? 'spinner' : 'history'"
        class="mr-1 text-gray-600"
        size="sm"
        :spin="isLoading"
        aria-hidden="true"
        data-test="revision-selector-icon"
      />
      <span v-if="selectedRevision && !isSelectedRevisionRoot" data-test="revision-selector-label">
        {{ selectedRevision.revisionLabel }}
      </span>
      <span v-else data-test="revision-selector-historical-name">
        {{ historicalRevisionName }}
      </span>
    </button>

    <button
      v-if="enableHistoricalRevisions && selectedRevision && !isSelectedRevisionRoot && rootRevision"
      class="ml-2 text-gray-500 hover:text-gray-700 transition-colors"
      aria-label="Show to current revision"
      title="Show current revision"
      data-test="revision-selector-reset-button"
      @click="showCurrentRevision"
    >
      <BaseIcon
        icon="times"
        size="sm"
        aria-hidden="true"
      />
    </button>

    <RevisionSelectorModal
      v-if="isOpen"
      :revisions="revisions"
      :selected-revision="selectedRevision"
      :is-loading="isLoading"
      :root-revision="rootRevision"
      data-test="revision-selector-modal"
      @close="closeModal"
      @select="localSelectRevision"
    />
  </div>
</template>

<script>
import { ref, onMounted } from '@nuxtjs/composition-api'
import useRevisions from '@/composables/useRevisions'
import RevisionSelectorModal from '@/components/RevisionSelectorModal.vue'

export default {
  name: 'RevisionSelector',
  components: {
    RevisionSelectorModal
  },
  props: {
    entityType: {
      type: String,
      required: true,
      validator: (value) => ['courses', 'programs'].includes(value)
    },
    entityId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const isOpen = ref(false)
    const {
      revisions,
      selectedRevision,
      fetchRevisions,
      enableHistoricalRevisions,
      historicalRevisionName,
      isLoading,
      selectRevision,
      isSelectedRevisionRoot,
      rootRevision
    } = useRevisions(props.entityType, props.entityId)

    onMounted(async () => {
      if (enableHistoricalRevisions.value) {
        await fetchRevisions()
      }
    })

    const openModal = () => {
      isOpen.value = true
    }

    const closeModal = () => {
      isOpen.value = false
    }

    const localSelectRevision = (revision) => {
      selectRevision(revision, closeModal)
    }

    const showCurrentRevision = () => {
      if (rootRevision) {
        selectRevision(rootRevision)
      }
    }

    return {
      isOpen,
      revisions,
      selectedRevision,
      openModal,
      closeModal,
      localSelectRevision,
      enableHistoricalRevisions,
      isSelectedRevisionRoot,
      historicalRevisionName,
      isLoading,
      rootRevision,
      showCurrentRevision
    }
  }
}
</script>
